import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || '';

export default function Landing() {
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState(null); // 'ok' | 'error'

  async function handleSubscribe(e) {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setEmailStatus('ok');
        setEmail('');
      } else {
        setEmailStatus('error');
      }
    } catch {
      setEmailStatus('error');
    }
  }

  return (
    <div style={styles.root}>
      {/* Background orbs */}
      <div style={styles.orb1} />
      <div style={styles.orb2} />

      {/* Nav */}
      <nav style={styles.nav}>
        <span style={styles.logo}>datum<span style={styles.logoAccent}>o</span>tiv</span>
        <Link to="/survey" style={styles.navCta}>Take the survey →</Link>
      </nav>

      {/* Hero */}
      <main style={styles.main}>
        <div style={styles.hero}>
          <div style={styles.preLabel}>
            <span style={styles.preLine} />
            Early access · India
          </div>

          <h1 style={styles.headline}>
            <span style={styles.headlineSub}>Most things don't break suddenly.</span>
            <span style={styles.headlineLarge}>They warn</span>
            <span style={styles.headlineLarge}>
              you <span style={styles.headlineAccent}>first.</span>
            </span>
          </h1>

          <p style={styles.subtext}>
            We're building something for people who want to stay ahead of the things they own.
            Fewer surprises. More certainty. Starting with what matters most to you.
          </p>

          <div style={styles.ctaGroup}>
            <Link to="/survey" style={styles.btnPrimary}>
              Share your experience <span style={styles.arrow}>→</span>
            </Link>

            <div style={styles.emailBlock}>
              <span style={styles.emailLabel}>Get early access</span>
              {emailStatus === 'ok' ? (
                <p style={styles.emailSuccess}>You're on the list. We'll be in touch.</p>
              ) : (
                <form onSubmit={handleSubscribe} style={styles.emailRow}>
                  <input
                    type="email"
                    required
                    placeholder="your@email.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    style={styles.emailInput}
                  />
                  <button type="submit" style={styles.emailBtn}>Notify me</button>
                </form>
              )}
              {emailStatus === 'error' && (
                <p style={styles.emailError}>Something went wrong. Try again.</p>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <span style={styles.footerText}>© 2025 Datumotiv. All rights reserved.</span>
        <span style={styles.footerBadge}>🇮🇳 Built in India</span>
      </footer>
    </div>
  );
}

const styles = {
  root: {
    minHeight: '100vh',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  orb1: {
    position: 'fixed',
    width: 600,
    height: 600,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(201,168,76,0.07) 0%, rgba(100,80,200,0.03) 50%, transparent 70%)',
    top: '50%',
    left: '30%',
    transform: 'translate(-50%,-50%)',
    filter: 'blur(60px)',
    pointerEvents: 'none',
    animation: 'none',
  },
  orb2: {
    position: 'fixed',
    width: 400,
    height: 400,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(80,100,220,0.04) 0%, transparent 70%)',
    bottom: '10%',
    right: '15%',
    filter: 'blur(80px)',
    pointerEvents: 'none',
  },
  nav: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    padding: '28px 48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 10,
  },
  logo: {
    fontFamily: "'Syne', sans-serif",
    fontWeight: 700,
    fontSize: 18,
    letterSpacing: '-0.02em',
    color: '#ede8df',
    textDecoration: 'none',
  },
  logoAccent: {
    color: '#c9a84c',
  },
  navCta: {
    fontSize: 13,
    color: '#9a9488',
    textDecoration: 'none',
    letterSpacing: '0.02em',
  },
  main: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    padding: '120px 48px 100px',
    maxWidth: 1100,
    margin: '0 auto',
    width: '100%',
  },
  hero: {
    maxWidth: 720,
  },
  preLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#c9a84c',
    fontWeight: 500,
    marginBottom: 32,
  },
  preLine: {
    display: 'block',
    width: 20,
    height: 1,
    background: '#c9a84c',
  },
  headline: {
    marginBottom: 28,
    display: 'flex',
    flexDirection: 'column',
  },
  headlineSub: {
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 300,
    fontSize: 'clamp(16px, 2.5vw, 22px)',
    color: '#9a9488',
    marginBottom: 4,
    letterSpacing: '-0.01em',
  },
  headlineLarge: {
    fontFamily: "'Instrument Serif', serif",
    fontStyle: 'italic',
    fontSize: 'clamp(44px, 7vw, 82px)',
    lineHeight: 1,
    letterSpacing: '-0.02em',
    color: '#ede8df',
  },
  headlineAccent: {
    color: '#c9a84c',
    fontStyle: 'normal',
  },
  subtext: {
    fontSize: 'clamp(15px, 1.8vw, 18px)',
    color: '#9a9488',
    fontWeight: 300,
    lineHeight: 1.7,
    maxWidth: 520,
    marginBottom: 52,
  },
  ctaGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 28,
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px 28px',
    background: '#ede8df',
    color: '#090910',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 15,
    fontWeight: 500,
    textDecoration: 'none',
    width: 'fit-content',
    letterSpacing: '-0.01em',
    transition: 'background 0.2s',
  },
  arrow: {
    transition: 'transform 0.2s',
  },
  emailBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  emailLabel: {
    fontSize: 12,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#5c5852',
  },
  emailRow: {
    display: 'flex',
    border: '1px solid rgba(237,232,223,0.07)',
    width: 'fit-content',
  },
  emailInput: {
    background: 'transparent',
    border: 'none',
    outline: 'none',
    padding: '12px 18px',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    color: '#ede8df',
    width: 260,
  },
  emailBtn: {
    background: 'rgba(237,232,223,0.06)',
    border: 'none',
    borderLeft: '1px solid rgba(237,232,223,0.07)',
    padding: '12px 20px',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13,
    color: '#9a9488',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  emailSuccess: {
    fontSize: 14,
    color: '#c9a84c',
  },
  emailError: {
    fontSize: 12,
    color: '#c0604a',
    marginTop: 4,
  },
  footer: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '20px 48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTop: '1px solid rgba(237,232,223,0.07)',
    zIndex: 10,
  },
  footerText: {
    fontSize: 12,
    color: '#5c5852',
    letterSpacing: '0.02em',
  },
  footerBadge: {
    fontSize: 12,
    color: '#5c5852',
  },
};
